import React from 'react';

import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import {SimpleRule, SimpleRuleInput} from './types';

export interface ValidationRuleModalContainerProps {
  schemaField: SchemaField;
  onRequestSubmit: (simpleRulesState: SimpleRulesState) => void;
  onRequestClose: () => void;
  enableMultiSelectOption?: boolean;
  schemaFieldSelectors?: Array<SchemaFieldSelector>;
}

export interface ValidationRuleConstantInput {
  [inputKey: string]: SimpleRuleInput;
}

export interface SimpleRuleActions {
  handleUpdateSimpleRule: (
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput
  ) => void;
  handleRemoveSimpleRule: (validationRuleTemplateId: string) => void;
  handleUpdateLegacySimpleRules: (legacySimpleRules: LegacySimpleRules) => void;
}

function buildNewSimpleRule(
  simpleRules: Array<SimpleRule>,
  validationRuleTemplateId: string,
  constantInputs: ValidationRuleConstantInput
): SimpleRule {
  const existingRule = simpleRules.find((rule) => rule.validationRuleTemplateId === validationRuleTemplateId);
  if (existingRule) {
    return {
      validationRuleId: existingRule.validationRuleId,
      validationRuleTemplateId,
      constantInputs: {
        ...existingRule.constantInputs,
        ...constantInputs,
      },
    };
  }
  return {
    validationRuleTemplateId,
    constantInputs,
  };
}

export interface LegacySimpleRules {
  isRequired: boolean;
  isMulti: boolean;
}

export interface SimpleRulesState {
  simpleRules: Array<SimpleRule>;
  legacySimpleRules: LegacySimpleRules;
}

export default function useSimpleRules(schemaField: SchemaField): [SimpleRulesState, SimpleRuleActions] {
  const initialSimpleRulesForModal = schemaField.simpleRules || [];
  const [simpleRules, setSimpleRules] = React.useState<Array<SimpleRule>>(initialSimpleRulesForModal);
  const initialLegacySimpleRules: LegacySimpleRules = {
    isRequired: schemaField.isRequired,
    isMulti: schemaField.isMulti,
  };
  const [legacySimpleRules, setLegacySimpleRules] =
    React.useState<LegacySimpleRules>(initialLegacySimpleRules);

  function handleUpdateSimpleRule(
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput
  ): void {
    const newSimpleRule: SimpleRule = buildNewSimpleRule(
      simpleRules,
      validationRuleTemplateId,
      constantInputs
    );
    setSimpleRules([
      ...simpleRules.filter(
        (rule) => rule.validationRuleTemplateId !== newSimpleRule.validationRuleTemplateId
      ),
      newSimpleRule,
    ]);
  }

  function handleRemoveSimpleRule(validationRuleTemplateId: string): void {
    setSimpleRules(simpleRules.filter((rule) => rule.validationRuleTemplateId !== validationRuleTemplateId));
  }

  return [
    {
      simpleRules,
      legacySimpleRules,
    },
    {
      handleUpdateSimpleRule,
      handleRemoveSimpleRule,
      handleUpdateLegacySimpleRules: (legacySimpleRules: LegacySimpleRules) => {
        setLegacySimpleRules(legacySimpleRules);
      },
    },
  ];
}
